import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { AboutDeveloperImage, AboutStaffVideo } from "../components/AboutMedia";
import { useSeo } from "../lib/useSeo";
import { ABOUT_TITLE, ABOUT_TAGLINE, ABOUT_INTRO, ABOUT_SECTIONS, ABOUT_DEVELOPER } from "../lib/aboutContent";

const AboutVideo = ({ discovery = false }) => (
  <div className={discovery ? "mb-8 w-full" : "my-8 w-full max-w-sm mx-auto"} data-testid={discovery ? "about-discovery-video-section" : "about-video-section"}>
    <div className={`relative ${discovery ? "aspect-video" : "aspect-[9/16]"} overflow-hidden rounded-lg bg-[#0B0F0D] border border-[#D4AF37]/40`}>
      <iframe
        data-testid={discovery ? "about-discovery-youtube-player" : "about-youtube-player"}
        className="absolute inset-0 h-full w-full"
        src={`https://www.youtube-nocookie.com/embed/${discovery ? "90wHg4fOaXc" : "k_HR2n5r1Q8"}`}
        title={discovery ? "Step by step: how to use XpressPrompts AI prompt shortcut commands" : "How AI Shorthand works — XpressPrompts introduction"}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
    <a href={discovery ? "https://youtu.be/90wHg4fOaXc?si=gYjRd1a_jqPg_aMG" : "https://youtube.com/shorts/k_HR2n5r1Q8?si=bHXZnHW-Ie2zlG5S"} target="_blank" rel="noopener noreferrer"
      data-testid={discovery ? "about-discovery-watch-youtube" : "about-watch-youtube"} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-emerald-950 underline underline-offset-4 transition-colors">
      Watch on YouTube <ExternalLink size={14} aria-hidden="true" />
    </a>
  </div>
);

const AboutSection = ({ section }) => (
  <section aria-labelledby={`about-heading-${section.id}`} data-testid={`about-section-${section.id}`} className="border-t border-slate-200 pt-8">
    {section.id === "multiple-ai-tools" && <AboutStaffVideo />}
    {section.id === "discover-ai" && <AboutVideo discovery />}
    <h2 id={`about-heading-${section.id}`} data-testid={`about-heading-${section.id}`} className="font-display text-base md:text-lg font-bold text-slate-950">{section.title}</h2>
    {section.video && <AboutVideo />}
    <div className="mt-4 space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed">
      {section.paragraphs.map((paragraph, index) => <p key={paragraph} data-testid={`about-${section.id}-paragraph-${index + 1}`}>{paragraph}</p>)}
      {section.steps && <ol data-testid="about-steps" className="list-decimal pl-6 space-y-3 marker:font-semibold marker:text-emerald-800">
        {section.steps.map(({ label, text }, index) => <li key={label} data-testid={`about-step-${index + 1}`} className="pl-1"><strong className="text-slate-900">{label}</strong> — {text}</li>)}
      </ol>}
      {section.bullets && <ul data-testid="about-audiences" className="list-disc pl-6 space-y-3 marker:text-emerald-700">
        {section.bullets.map((text, index) => <li key={text} data-testid={`about-audience-${index + 1}`} className="pl-1">{text}</li>)}
      </ul>}
      {section.closing && <p data-testid={`about-${section.id}-closing`}>{section.closing}</p>}
    </div>
    {section.dictionaryLink && <Button asChild className="mt-6 bg-emerald-800 hover:bg-emerald-900 text-white gap-2">
      <Link to="/categories" data-testid="about-explore-dictionary">Explore the dictionary <ArrowRight size={16} aria-hidden="true" /></Link>
    </Button>}
  </section>
);

export default function AboutPage() {
  useSeo({ title: "About XpressPrompts — Universal Short Prompt Commands Dictionary", description: ABOUT_TAGLINE });
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 min-h-[60vh] break-words" data-testid="about-page">
      <header>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-normal leading-tight text-slate-950" data-testid="about-page-title">{ABOUT_TITLE}</h1>
        <p className="mt-6 text-base sm:text-lg font-semibold text-emerald-800" data-testid="about-tagline">{ABOUT_TAGLINE}</p>
      </header>
      <div className="mt-8 space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed" data-testid="about-introduction">
        {ABOUT_INTRO.map((paragraph, index) => <p key={paragraph} data-testid={`about-intro-paragraph-${index + 1}`}>{paragraph}</p>)}
      </div>
      <div className="mt-12 space-y-12">
        {ABOUT_SECTIONS.map((section) => <AboutSection key={section.id} section={section} />)}
      </div>
      <section aria-labelledby="about-developer-title" className="mt-14 border-t-2 border-[#D4AF37]/60 pt-10" data-testid="about-developer">
        <AboutDeveloperImage />
        <h2 id="about-developer-title" data-testid="about-developer-title" className="font-display text-base md:text-lg font-bold">About the Developer</h2>
        <h3 data-testid="about-developer-name" className="mt-4 font-display font-bold text-base md:text-lg text-emerald-900 leading-relaxed">{ABOUT_DEVELOPER.heading}</h3>
        <div className="mt-4 space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed">
          {ABOUT_DEVELOPER.paragraphs.map((paragraph, index) => <p key={paragraph} data-testid={`about-developer-paragraph-${index + 1}`}>{paragraph}</p>)}
        </div>
        <h3 data-testid="about-mission-title" className="mt-8 font-display font-bold text-base md:text-lg text-slate-950">MISSION</h3>
        <p data-testid="about-mission" className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{ABOUT_DEVELOPER.mission}</p>
      </section>
    </div>
  );
}